import { Text, useDisclosure } from "@chakra-ui/react";
import * as React from "react";

import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

// Custom components
import Card from "components/card/Card";
import ListTable, { ListCheck, ListHeader, ListRow } from "components/list/ListTable";
import { usePagination } from "components/list/TablePagination";
import { useListState } from "hooks/useListState";
import ModalForm from "components/modal/modalForm";

import ModalDelete from "components/modal/modalDelete";
import FieldClient from "components/user/FieldClient";
import ListMenuContact from "components/menu/ListMenuContact";


type TableContactType = {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  idclient: string;
  principal: boolean;
};

const columnHelper = createColumnHelper<TableContactType>();

// const columns = columnsDataCheck;
export default function ColumnTable(props: { tableData: any }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const modalDelete = useDisclosure();
  const { tableData } = props;
  const [sorting, setSorting] = useListState<SortingState>("contacts:tri", []);
  const [selectedContact, setSelectedContact] = React.useState<
    ContactType[] | null
  >(null);
  let defaultData = tableData;
  // Colonnes créées une seule fois : de nouvelles fonctions `cell` à chaque rendu
  // feraient remonter toutes les cellules (et relancer leurs lectures Firestore)
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("idclient", {
        id: "idclient",
        header: "Client",
        cell: (info) => (
          <Text fontWeight="700">
            <FieldClient
              id={info.row.original.idclient}
              field="raison_sociale"
              context="contact"
            />
          </Text>
        ),
      }),
      columnHelper.accessor("nom", {
        id: "nom",
        header: "Nom",
        meta: { width: { base: "170px", "2xl": "18%" } },
      }),
      columnHelper.accessor("prenom", {
        id: "prenom",
        header: "Prénom",
        meta: { width: { base: "130px", "2xl": "16%" } },
      }),
      columnHelper.accessor("email", {
        id: "email",
        header: "Email",
        meta: { width: { base: "280px", "2xl": "30%" } },
      }),
      columnHelper.accessor("principal", {
        id: "principal",
        header: "Principal",
        meta: { width: { base: "90px", "2xl": "9%" } },
        cell: (info) => <ListCheck checked={info.getValue()} />,
      }),
      columnHelper.display({
        id: "actions",
        meta: { width: "67px", align: "right" },
        cell: (info) => (
          <ListMenuContact
            contactrow={[info.row.original]}
            openmodal={onOpen}
            setselectedcontact={setSelectedContact}
            opendeletemodal={modalDelete.onOpen}
          />
        ),
      }),
    ],
    [onOpen, modalDelete.onOpen]
  );
  const [data, setData] = React.useState(() => [...defaultData]);
  const [pagination, setPagination] = usePagination(data.length);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      table.setPageIndex(0);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Lignes identifiées par leur document : après un tri ou un changement de page,
    // chaque cellule garde son enregistrement (et ne relance pas ses lectures Firestore)
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    // Garde la page après une modification ; le tri, les filtres et les ajouts reviennent à la première
    autoResetPageIndex: false,
  });
  // Un ajout revient à la première page, où le tri par défaut place la nouvelle ligne
  function handleFormData(newData: any[]) {
    setData(newData);
    if (!selectedContact) {
      table.setPageIndex(0);
    }
  }
  function handleAdd() {
    setSelectedContact(null);
    onOpen();
  }
  return (
    <Card flexDirection="column" w="100%" px="0px" pt="16px" pb="8px">
      <ListHeader
        title={`Liste des contacts (${data.length})`}
        addLabel="Ajouter un contact"
        onAdd={handleAdd}
      />
      <ListTable
        table={table}
        renderRow={(row) => <ListRow key={row.id} row={row} />}
      />
      <ModalForm<ContactType>
        isOpen={isOpen}
        onClose={onClose}
        newData={handleFormData}
        updateData={selectedContact}
        form="contact"
      />
      {selectedContact && (
        <ModalDelete
          isOpen={modalDelete.isOpen}
          onClose={modalDelete.onClose}
          idDelete={selectedContact[0].id}
          field={selectedContact[0].email}
          type={"contact"}
          newdata={setData}
        />
      )}
    </Card>
  );
}
